import React from 'react';

const TOOLS = [
  {
    name: 'search_products',
    title: 'Search Products',
    description: 'Search eYantra\'s catalog by keyword, category, brand, or price range.',
    readonly: true,
    params: [
      { name: 'search', desc: 'Keyword (e.g. "branded notebook", "polo t-shirt")' },
      { name: 'category', desc: 'Filter by category name' },
      { name: 'brand', desc: 'Filter by brand name' },
      { name: 'minPrice / maxPrice', desc: 'Price range in INR' },
      { name: 'page / size', desc: 'Pagination (max 20 per page)' },
    ],
  },
  {
    name: 'get_product_details',
    title: 'Get Product Details',
    description: 'Get full details for a product by its numeric ID including pricing with GST breakdown.',
    readonly: true,
    params: [
      { name: 'id', desc: 'Numeric product ID from search results' },
    ],
  },
  {
    name: 'get_categories',
    title: 'Get Categories',
    description: 'List all top-level product categories and their subcategories.',
    readonly: true,
    params: [],
  },
  {
    name: 'create_cart',
    title: 'Create Quote Request',
    description: 'Submit an RFQ (Request for Quote) with product selections. The eYantra team responds within 4 business hours with a formal quote. Returns a checkout URL.',
    readonly: false,
    params: [
      { name: 'name', desc: 'Customer\'s full name (required)' },
      { name: 'email', desc: 'Customer\'s email address (required)' },
      { name: 'phone', desc: 'Phone number (optional)' },
      { name: 'company', desc: 'Company or organisation name' },
      { name: 'notes', desc: 'Special requirements or branding instructions' },
      { name: 'items', desc: 'Array of { productId, quantity }' },
    ],
  },
  {
    name: 'get_quote_status',
    title: 'Get Quote Status',
    description: 'Check the status of an existing quote using the reference number and email.',
    readonly: true,
    params: [
      { name: 'ref', desc: 'Quote reference number (e.g. RFQ-CompanyName-123456)' },
      { name: 'email', desc: 'Email used when the quote was created' },
    ],
  },
];

export default function McpDocsPage() {
  return (
    <div style={{ maxWidth: 800, margin: '60px auto', padding: '0 24px 80px', fontFamily: 'inherit', color: '#1a1a1a', lineHeight: 1.7 }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>eYantra MCP Connector</h1>
      <p style={{ color: '#888', fontSize: 14, marginBottom: 32 }}>Model Context Protocol — Documentation</p>

      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Overview</h2>
        <p>
          The eYantra MCP connector lets AI assistants (Claude, ChatGPT, Gemini) search India's
          leading corporate gifting catalog, retrieve product details, and submit bulk quote requests
          on behalf of users — all without leaving the chat interface.
        </p>
        <p>
          eYantra has served 1500+ corporate clients across India since 2000, offering 500+ branded
          and customisable products for employee gifting, onboarding kits, and event merchandise.
        </p>
      </section>

      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Endpoint</h2>
        <code style={{ background: '#f5f5f5', padding: '10px 16px', borderRadius: 8, display: 'block', fontSize: 14 }}>
          https://mcp.shop.eyantra.com/mcp
        </code>
        <p style={{ marginTop: 12, fontSize: 14, color: '#555' }}>
          Transport: StreamableHTTP &nbsp;·&nbsp; Authentication: None (public) &nbsp;·&nbsp; Protocol: MCP 1.0
        </p>
      </section>

      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Available Tools</h2>
        {TOOLS.map((tool) => (
          <div key={tool.name} style={{ border: '1px solid #eee', borderRadius: 10, padding: '20px 24px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4, fontSize: 13 }}>{tool.name}</code>
              <span style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 20,
                background: tool.readonly ? '#e8f5e9' : '#fff3e0',
                color: tool.readonly ? '#2e7d32' : '#e65100',
                fontWeight: 600,
              }}>
                {tool.readonly ? 'read-only' : 'write'}
              </span>
            </div>
            <p style={{ margin: '4px 0 12px', fontSize: 14 }}>{tool.description}</p>
            {tool.params.length > 0 && (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <tbody>
                  {tool.params.map((p) => (
                    <tr key={p.name} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 12px 5px 0', fontFamily: 'monospace', color: '#555', whiteSpace: 'nowrap' }}>{p.name}</td>
                      <td style={{ padding: '5px 0', color: '#666' }}>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </section>

      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Authentication</h2>
        <p>No authentication required. The connector is publicly accessible.</p>
      </section>

      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Add to Claude</h2>
        <p>Go to <strong>claude.ai → Settings → Connectors → Add custom connector</strong> and paste:</p>
        <code style={{ background: '#f5f5f5', padding: '10px 16px', borderRadius: 8, display: 'block', fontSize: 14 }}>
          https://mcp.shop.eyantra.com/mcp
        </code>
      </section>

      <section>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Contact</h2>
        <p>
          For support or integration queries:{' '}
          <a href="mailto:tech@eyantra.com" style={{ color: '#E05010' }}>tech@eyantra.com</a>
        </p>
      </section>
    </div>
  );
}